/**
 * Macro manager — list / add / edit / delete typed-trigger + voice-alias
 * report snippets. Clinic-default with optional personal override.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Macro, loadMacros, newMacroId, saveMacros } from '../utils/macros';
import { SettingScope } from '../utils/userSettings';
import styles from './HeaderDesignerPage.module.css';

const MODALITIES = ['', 'CT', 'MR', 'CR', 'DX', 'MG', 'US', 'XA', 'NM', 'PT'];

export default function MacrosPage() {
  const navigate = useNavigate();
  const [macros, setMacros] = useState<Macro[]>([]);
  const [currentScope, setCurrentScope] = useState<SettingScope | undefined>();
  const [scope, setScope] = useState<SettingScope>('clinic');
  const [editing, setEditing] = useState<Macro | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  async function reload() {
    const r = await loadMacros();
    setMacros(r.macros);
    setCurrentScope(r.scope);
  }

  useEffect(() => {
    document.title = 'Report Macros';
    void reload();
  }, []);

  function createNew() {
    setEditing({
      id: newMacroId(),
      trigger: '',
      voiceAliases: [],
      expansion: '',
      modality: '',
      description: '',
    });
  }

  function editMacro(m: Macro) {
    setEditing({ ...m, voiceAliases: [...(m.voiceAliases || [])] });
  }

  function updateEditing(patch: Partial<Macro>) {
    if (!editing) return;
    setEditing({ ...editing, ...patch });
  }

  function validate(m: Macro): string | null {
    if (!m.trigger.trim()) return 'Trigger is required.';
    if (/\s|\\/.test(m.trigger)) return 'Trigger cannot contain spaces or backslashes.';
    if (!m.expansion.trim()) return 'Expansion text is required.';
    return null;
  }

  async function persistList(list: Macro[]) {
    setSaving(true);
    setMessage('');
    const ok = await saveMacros(list, scope);
    setSaving(false);
    if (ok) {
      setMessage(scope === 'clinic' ? 'Saved as clinic default' : 'Saved as personal override');
    } else {
      setMessage('Save failed — changes not persisted');
    }
    setTimeout(() => setMessage(''), 2200);
    return ok;
  }

  async function commit() {
    if (!editing) return;
    const err = validate(editing);
    if (err) { alert(err); return; }
    const clean: Macro = {
      ...editing,
      trigger: editing.trigger.trim(),
      voiceAliases: editing.voiceAliases.map((a) => a.trim()).filter(Boolean),
      expansion: editing.expansion,
      modality: (editing.modality || '').trim().toUpperCase() || undefined,
      description: editing.description?.trim() || undefined,
    };
    const next = macros.some((x) => x.id === clean.id)
      ? macros.map((x) => x.id === clean.id ? clean : x)
      : [...macros, clean];
    setMacros(next);
    setEditing(null);
    // Auto-save so users don't have to remember a separate "Save" click
    await persistList(next);
    // Re-pull scope label (a first clinic save changes "Currently: ..." to clinic)
    await reload();
  }

  async function remove(id: string) {
    if (!confirm('Delete this macro?')) return;
    const next = macros.filter((m) => m.id !== id);
    setMacros(next);
    await persistList(next);
    await reload();
  }

  /** Manual re-save — kept for the case where the user changes scope after
   *  editing, so they can promote the current list from personal to clinic
   *  (or vice versa) without editing again. */
  async function persist() {
    await persistList(macros);
    await reload();
  }

  return (
    <div className={styles.page}>
      <div className={styles.topbar}>
        <button className={styles.backBtn} onClick={() => navigate('/worklist')}>← Back</button>
        <h2 className={styles.title}>Report Macros</h2>
        {currentScope && (
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
            background: currentScope === 'user' ? '#d69e2e' : '#1a4971',
            color: 'white', textTransform: 'uppercase', letterSpacing: 0.5,
          }}>
            Currently: {currentScope === 'user' ? 'Personal override' : 'Clinic default'}
          </span>
        )}
        <div style={{ flex: 1 }} />

        <div style={{ display: 'flex', gap: 10, fontSize: 12, color: 'white' }}>
          <label style={{ cursor: 'pointer' }}>
            <input type="radio" checked={scope === 'clinic'} onChange={() => setScope('clinic')} /> Clinic
          </label>
          <label style={{ cursor: 'pointer' }}>
            <input type="radio" checked={scope === 'user'} onChange={() => setScope('user')} /> Just me
          </label>
        </div>

        {message && <span className={styles.message}>{message}</span>}
        <button
          className={styles.primaryBtn}
          onClick={persist}
          disabled={saving}
          title={
            scope === 'clinic'
              ? 'Resave the whole list as the clinic default (also happens automatically on Add/Delete)'
              : 'Resave the whole list as your personal override (also happens automatically on Add/Delete)'
          }
        >
          {saving ? 'Saving…' : scope === 'clinic' ? 'Resave for Clinic' : 'Resave for Me'}
        </button>
      </div>

      <div className={styles.body}>
        <div className={styles.designer} style={{ width: 380 }}>
          <h3 className={styles.sectionTitle}>Macros ({macros.length})</h3>
          <div style={{ fontSize: 11, color: '#718096', marginBottom: 8 }}>
            Type <code>\trigger</code> then press space/tab/enter to expand.
            Or dictate a voice alias to insert the expansion.
          </div>
          <button
            className={styles.smallBtn}
            onClick={createNew}
            style={{ marginBottom: 10 }}
          >+ New Macro</button>

          {macros.length === 0 && (
            <div style={{ fontSize: 12, color: '#718096', padding: 10 }}>
              No macros yet. Click "New Macro" to create one.
            </div>
          )}

          {macros.map((m) => (
            <div
              key={m.id}
              className={styles.rowBlock}
              style={{
                cursor: 'pointer',
                borderColor: editing?.id === m.id ? '#2b6cb0' : undefined,
              }}
              onClick={() => editMacro(m)}
            >
              <div className={styles.rowHead}>
                <code style={{ background: '#ebf8ff', padding: '1px 6px', borderRadius: 3, fontSize: 11 }}>
                  \{m.trigger}
                </code>
                {m.modality && (
                  <span style={{ fontSize: 10, color: '#2b6cb0', fontWeight: 600 }}>{m.modality}</span>
                )}
                <div style={{ flex: 1 }} />
                <button
                  className={styles.smallDangerBtn}
                  onClick={(e) => { e.stopPropagation(); remove(m.id); }}
                >Delete</button>
              </div>
              <div style={{ fontSize: 11, color: '#4a5568', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {m.expansion.slice(0, 90)}{m.expansion.length > 90 ? '…' : ''}
              </div>
              {m.voiceAliases?.length > 0 && (
                <div style={{ fontSize: 10, color: '#718096', marginTop: 3 }}>
                  🎙 {m.voiceAliases.join(' · ')}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className={styles.preview}>
          <div className={styles.previewLabel}>
            {editing ? (macros.some((x) => x.id === editing.id) ? 'Edit macro' : 'New macro') : 'Select a macro or create new'}
          </div>
          <div className={styles.previewFrame} style={{ padding: 20, minHeight: 400 }}>
            {editing ? (
              <>
                <label className={styles.sectionTitle} style={{ marginTop: 0 }}>Trigger *</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 12 }}>
                  <span style={{ fontSize: 16, color: '#2b6cb0', fontWeight: 700 }}>\</span>
                  <input
                    className={styles.input}
                    value={editing.trigger}
                    onChange={(e) => updateEditing({ trigger: e.target.value.replace(/\s/g, '') })}
                    placeholder="lungnormal"
                    autoFocus
                    style={{ flex: 1 }}
                  />
                </div>

                <label className={styles.sectionTitle}>Voice aliases (optional)</label>
                <div style={{ fontSize: 11, color: '#718096', marginBottom: 6 }}>
                  Comma-separated. Matched against the final dictation phrase (case-insensitive).
                </div>
                <input
                  className={styles.input}
                  value={editing.voiceAliases.join(', ')}
                  onChange={(e) => updateEditing({
                    voiceAliases: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                  })}
                  placeholder="normal lungs, lung normal"
                  style={{ marginBottom: 12 }}
                />

                <label className={styles.sectionTitle}>Modality (optional)</label>
                <select
                  className={styles.input}
                  value={editing.modality || ''}
                  onChange={(e) => updateEditing({ modality: e.target.value })}
                  style={{ marginBottom: 12 }}
                >
                  {MODALITIES.map((m) => (
                    <option key={m} value={m}>{m || '(any)'}</option>
                  ))}
                </select>

                <label className={styles.sectionTitle}>Description (optional)</label>
                <input
                  className={styles.input}
                  value={editing.description || ''}
                  onChange={(e) => updateEditing({ description: e.target.value })}
                  placeholder="Normal lung findings — chest CT"
                  style={{ marginBottom: 12 }}
                />

                <label className={styles.sectionTitle}>Expansion text *</label>
                <textarea
                  value={editing.expansion}
                  onChange={(e) => updateEditing({ expansion: e.target.value })}
                  rows={10}
                  placeholder={'The lungs are clear.\nNo focal consolidation, effusion, or pneumothorax.'}
                  style={{
                    width: '100%', padding: 8, border: '1px solid #cbd5e0', borderRadius: 3,
                    fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical',
                  }}
                />

                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button className={styles.smallBtn} onClick={commit}>
                    {macros.some((x) => x.id === editing.id) ? 'Update' : 'Add'}
                  </button>
                  <button className={styles.smallDangerBtn} onClick={() => setEditing(null)}>
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <div style={{ color: '#718096', fontSize: 13 }}>
                Pick a macro from the left to edit it, or click "New Macro".
                <br /><br />
                Click <strong>Save for Clinic/Me</strong> in the top-right to persist your changes.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
